/* ==========================================================================
   Quas motion — vanilla custom elements
   --------------------------------------------------------------------------
   Small custom elements and scene hooks, no framework and no dependency on
   Horizon JS.

   <quas-reveal>
     Adds the `is-in` class the first time it scrolls into view.
     Attributes:
       data-axis="y" (default) | "x" | "none"
       data-scale (presence)   = adds a small scale-in
       data-delay="0..900"     = ms, capped, also settable via CSS var
       data-threshold="0..1"   = visibility threshold (default 0.15)
       data-once (default true) — observer disconnects after first hit

   <quas-marquee data-direction="forward" | "reverse">
     Duplicates its single child track once on connect so the keyframe
     loop from 0 → -50% is seamless. Pauses on hover/focus-within via CSS.

   Both respect prefers-reduced-motion at the CSS layer; this script only
   adds class hooks and DOM, so reduced motion users still see the final
   layout immediately.
   ========================================================================== */

(() => {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */

  class QuasReveal extends HTMLElement {
    static observer = null;
    static registry = new WeakMap();

    static getObserver() {
      if (QuasReveal.observer) return QuasReveal.observer;
      QuasReveal.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target;
            el.classList.add('is-in');
            const meta = QuasReveal.registry.get(el);
            if (meta && meta.once !== false) {
              QuasReveal.observer.unobserve(el);
              QuasReveal.registry.delete(el);
            }
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
      );
      return QuasReveal.observer;
    }

    connectedCallback() {
      if (prefersReducedMotion) {
        this.classList.add('is-in');
        return;
      }

      const delay = Math.max(0, Math.min(900, Number(this.dataset.delay) || 0));
      if (delay) this.style.setProperty('--quas-reveal-delay', `${delay}ms`);

      const once = this.dataset.once !== 'false';
      QuasReveal.registry.set(this, { once });
      QuasReveal.getObserver().observe(this);
    }

    disconnectedCallback() {
      if (QuasReveal.observer) QuasReveal.observer.unobserve(this);
      QuasReveal.registry.delete(this);
    }
  }

  /* --------------------------------------------------------------- marquee */

  class QuasMarquee extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.quas-marquee__track');
      if (!track || track.dataset.cloned === 'true') return;
      track.dataset.cloned = 'true';

      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.appendChild(clone);
    }
  }

  /* --------------------------------------------------------- letter stagger */
  /*
   * <quas-stagger> wraps each word + character so CSS can fade letters in
   * sequence. The original text remains in an aria-hidden parent for screen
   * readers via aria-label on the host element.
   *
   *   data-delay   ms before first letter starts (default 0)
   *   data-step    NOT yet used — step is baked into CSS for now
   */
  class QuasStagger extends HTMLElement {
    static observer = null;

    static getObserver() {
      if (QuasStagger.observer) return QuasStagger.observer;
      QuasStagger.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            QuasStagger.observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.2 }
      );
      return QuasStagger.observer;
    }

    connectedCallback() {
      if (this.dataset.split === 'done') return;
      this.dataset.split = 'done';

      const sourceText = (this.textContent || '').trim();
      if (!sourceText) return;
      if (!this.hasAttribute('aria-label')) {
        this.setAttribute('aria-label', sourceText);
      }

      const delay = Math.max(0, Math.min(2000, Number(this.dataset.delay) || 0));
      this.style.setProperty('--qs-delay', `${delay}ms`);

      // Rebuild content: words wrap so spacing breaks naturally between them.
      const frag = document.createDocumentFragment();
      const words = sourceText.split(/(\s+)/);
      let charIndex = 0;
      for (const part of words) {
        if (!part) continue;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(' '));
          continue;
        }
        const wordEl = document.createElement('span');
        wordEl.className = 'qs-word';
        wordEl.setAttribute('aria-hidden', 'true');
        for (const ch of [...part]) {
          const span = document.createElement('span');
          span.className = 'qs-char';
          span.style.setProperty('--qs-index', String(charIndex++));
          span.textContent = ch;
          wordEl.appendChild(span);
        }
        frag.appendChild(wordEl);
      }
      this.replaceChildren(frag);

      if (prefersReducedMotion) {
        this.classList.add('is-in');
        return;
      }
      QuasStagger.getObserver().observe(this);
    }

    disconnectedCallback() {
      if (QuasStagger.observer) QuasStagger.observer.unobserve(this);
    }
  }

  /* --------------------------------------------------------- parallax */
  /*
   * Lightweight scroll parallax. Reads data-intensity (default 0.15) and
   * updates --quas-parallax-y as the element passes through the viewport.
   * Single shared rAF loop drives all instances. Disabled under reduced
   * motion or when the user is on a small viewport (motion + layout cost
   * outweighs the visual gain).
   */
  const parallaxNodes = new Set();
  let parallaxFrame = null;

  function tickParallax() {
    parallaxFrame = null;
    if (!parallaxNodes.size) return;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    for (const node of parallaxNodes) {
      const rect = node.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) continue;
      const intensity = Number(node.dataset.intensity) || 0.15;
      const center = rect.top + rect.height / 2;
      const offset = (center - vh / 2) * intensity * -1;
      node.style.setProperty('--quas-parallax-y', `${offset.toFixed(2)}px`);
    }
  }

  function scheduleParallax() {
    if (parallaxFrame) return;
    parallaxFrame = window.requestAnimationFrame(tickParallax);
  }

  function setupParallax() {
    if (prefersReducedMotion) return;
    const smallViewport =
      window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    if (smallViewport) return;
    const nodes = document.querySelectorAll('[data-parallax]');
    nodes.forEach((node) => parallaxNodes.add(node));
    if (!parallaxNodes.size) return;
    window.addEventListener('scroll', scheduleParallax, { passive: true });
    window.addEventListener('resize', scheduleParallax, { passive: true });
    scheduleParallax();
  }

  /* --------------------------------------------------------- spotlight */
  /*
   * Tracks pointer position within .quas-spotlight elements so the CSS
   * radial-gradient ::before follows the cursor. Pure CSS variable update,
   * no layout cost.
   */
  function setupSpotlight() {
    if (prefersReducedMotion) return;
    document.addEventListener(
      'pointermove',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const host = target.closest('.quas-spotlight');
        if (!host) return;
        const rect = host.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        host.style.setProperty('--qs-x', `${x}%`);
        host.style.setProperty('--qs-y', `${y}%`);
      },
      { passive: true }
    );
  }

  /* --------------------------------------------------------- motion scenes */
  /*
   * Opt-in section choreography. Sections remain fully readable before this
   * script runs; the observer only settles transforms, masks and group rhythm.
   * This keeps the block library useful in no-JavaScript and hidden-tab tests.
   */
  const sceneSelector = '[data-quas-motion-scene]';
  const mountedScenes = new WeakSet();
  const sceneObserver =
    !prefersReducedMotion && typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-in');
              sceneObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -12% 0px', threshold: 0.16 }
        )
      : null;

  /*
   * A scene that tells a short sequence the visitor should watch, such as
   * the struck-through search on the founder story, opts into
   * data-quas-motion-trigger="reading": it starts once its top reaches the
   * reading line, a little above the middle of the screen, rather than as
   * soon as it peeks in from below.
   */
  const readingObserver = sceneObserver
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            // A scene already scrolled past, after a jump down the page,
            // counts as read, so nothing stays in its starting state.
            if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) continue;
            entry.target.classList.add('is-in');
            readingObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -42% 0px', threshold: 0 }
      )
    : null;

  function setupMotionScenes(root = document) {
    const scenes = root.matches?.(sceneSelector)
      ? [root]
      : [...root.querySelectorAll(sceneSelector)];

    scenes.forEach((scene) => {
      if (mountedScenes.has(scene)) return;
      mountedScenes.add(scene);

      if (!sceneObserver) {
        scene.classList.add('is-in');
        return;
      }

      scene.classList.add('is-motion-ready');
      const observer =
        scene.dataset.quasMotionTrigger === 'reading' ? readingObserver : sceneObserver;
      observer.observe(scene);
    });
  }

  /* --------------------------------------------------------- award glint */
  /*
   * Every Great Taste mark catches the light once, as it comes into view:
   * `is-glinting` starts a single sweep drawn inside the mark's own shape.
   * Reduced motion and no-IntersectionObserver visitors keep the plain mark.
   */
  const glintSelector = '[data-quas-glint]';
  const mountedGlints = new WeakSet();
  const glintObserver =
    !prefersReducedMotion && typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-glinting');
              glintObserver.unobserve(entry.target);
            }
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.9 }
        )
      : null;

  function setupGlints(root = document) {
    if (!glintObserver) return;
    const marks = root.matches?.(glintSelector)
      ? [root]
      : [...root.querySelectorAll(glintSelector)];

    marks.forEach((mark) => {
      if (mountedGlints.has(mark)) return;
      mountedGlints.add(mark);
      glintObserver.observe(mark);
    });
  }

  /* ------------------------------------------------ animated disclosures */
  /*
   * Preserve native <details>/<summary> semantics, then progressively
   * Prefer the browser's native details-content interpolation where it is
   * available. WAAPI supplies the same height transition in older engines.
   * Reduced-motion visitors keep the native instant disclosure behaviour.
   */
  const enhancedDisclosures = new WeakSet();

  function setupDisclosureMotion(root = document) {
    const hasNativeDetailsTransition =
      typeof CSS !== 'undefined' &&
      CSS.supports?.('selector(details::details-content)') &&
      CSS.supports?.('interpolate-size: allow-keywords');

    if (
      prefersReducedMotion ||
      hasNativeDetailsTransition ||
      typeof Element === 'undefined' ||
      !('animate' in Element.prototype)
    ) {
      return;
    }

    const selector = '[data-quas-disclosure-motion]';
    const groups = root.matches?.(selector)
      ? [root]
      : [...root.querySelectorAll(selector)];

    groups.forEach((group) => {
      group.dataset.quasDisclosureEngine = 'waapi';

      group.querySelectorAll('details').forEach((details) => {
        if (enhancedDisclosures.has(details)) return;

        const summary = details.querySelector('summary');
        const panel = details.querySelector('.quas-acc__panel');
        if (!summary || !panel) return;

        enhancedDisclosures.add(details);
        let busy = false;

        const cleanup = () => {
          details.style.removeProperty('height');
          details.style.removeProperty('overflow');
          busy = false;
        };

        const expand = () => {
          details.style.overflow = 'hidden';
          details.open = true;

          const startHeight = summary.getBoundingClientRect().height;
          const endHeight = details.scrollHeight;
          details.style.height = `${startHeight}px`;

          window.requestAnimationFrame(() => {
            const animation = details.animate(
              { height: [`${startHeight}px`, `${endHeight}px`] },
              { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
            );

            animation.addEventListener('finish', cleanup, { once: true });
            animation.addEventListener('cancel', cleanup, { once: true });
          });
        };

        const collapse = () => {
          details.style.overflow = 'hidden';

          const startHeight = details.getBoundingClientRect().height;
          const endHeight = summary.getBoundingClientRect().height;
          const animation = details.animate(
            { height: [`${startHeight}px`, `${endHeight}px`] },
            { duration: 220, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
          );

          animation.addEventListener(
            'finish',
            () => {
              details.open = false;
              cleanup();
            },
            { once: true }
          );
          animation.addEventListener('cancel', cleanup, { once: true });
        };

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (busy) return;

          busy = true;
          if (details.open) collapse();
          else expand();
        });
      });
    });
  }

  if (typeof customElements !== 'undefined') {
    if (!customElements.get('quas-reveal')) {
      customElements.define('quas-reveal', QuasReveal);
    }
    if (!customElements.get('quas-marquee')) {
      customElements.define('quas-marquee', QuasMarquee);
    }
    if (!customElements.get('quas-stagger')) {
      customElements.define('quas-stagger', QuasStagger);
    }
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setupParallax();
        setupSpotlight();
        setupMotionScenes();
        setupGlints();
        setupDisclosureMotion();
      });
    } else {
      setupParallax();
      setupSpotlight();
      setupMotionScenes();
      setupGlints();
      setupDisclosureMotion();
    }

    document.addEventListener('shopify:section:load', (event) => {
      setupMotionScenes(event.target);
      setupGlints(event.target);
      setupDisclosureMotion(event.target);
    });

    // The product picker swaps #MainContent in place when the flavour changes.
    document.addEventListener('quas:picker:route', () => {
      const main = document.getElementById('MainContent');
      if (!main) return;
      setupMotionScenes(main);
      setupGlints(main);
      setupDisclosureMotion(main);
    });
  }
})();
